<template>
  <v-dialog
    :model-value="modelValue"
    max-width="960px"
    persistent
    scrollable
    data-testid="tpsv2-history-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="tpsv2-sub tpsv2-hist">
      <div class="tpsv2-head tpsv2-head--sub bg-primary text-white">
        <div class="tpsv2-head-mark"><v-icon size="19">mdi-history</v-icon></div>
        <div class="tpsv2-head-text">
          <h2 class="tpsv2-head-title">ประวัติการบันทึก</h2>
          <p class="tpsv2-head-sub">
            <template v-if="versionLabel">เวอร์ชัน {{ versionLabel }} · </template>
            ทุกครั้งที่กดบันทึกจะถูกเก็บเป็น 1 รอบ — กู้คืนได้ และการกู้คืนก็ถูกบันทึกเป็นรอบใหม่
          </p>
        </div>
        <span v-if="versionActive" class="tpsv2-livetag" data-testid="tpsv2-history-active-chip">
          <span class="tpsv2-livedot" aria-hidden="true" />
          ใช้งานอยู่
        </span>
        <v-btn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          color="white"
          aria-label="ปิดหน้าต่างประวัติ"
          data-testid="tpsv2-history-close-btn"
          @click="close"
        />
      </div>

      <v-card-text class="tpsv2-hist-body">
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="tpsv2-alert mb-4"
          data-testid="tpsv2-history-error"
        >
          {{ error }}
          <template #append>
            <v-btn size="small" variant="text" data-testid="tpsv2-history-reload-btn" @click="load">
              ลองใหม่
            </v-btn>
          </template>
        </v-alert>

        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="rows"
          :loading="loading"
          density="comfortable"
          :items-per-page="10"
          show-select
          item-value="snapshotID"
          :select-strategy="'page'"
          class="tpsv2-hist-table"
          data-testid="tpsv2-history-table"
        >
          <template v-slot:item.snapshotNo="{ item }">
            <div class="d-flex align-center flex-wrap ga-1">
              <v-chip size="small" variant="flat" :color="item.isCurrent ? 'success' : undefined">
                v{{ item.snapshotNo }}
              </v-chip>
              <v-chip
                v-if="item.isCurrent"
                size="x-small"
                variant="tonal"
                color="success"
                prepend-icon="mdi-check-circle"
              >
                Current
              </v-chip>
              <v-chip
                v-if="item.sourceAction === 'BASELINE'"
                size="x-small"
                variant="tonal"
                color="grey"
              >
                BASELINE
              </v-chip>
              <v-chip
                v-else-if="item.sourceAction === 'RESTORE'"
                size="x-small"
                variant="tonal"
                color="info"
              >
                RESTORE
                <v-tooltip v-if="item.restoredFromNo" activator="parent" location="top">
                  กู้คืนจาก v{{ item.restoredFromNo }}
                </v-tooltip>
              </v-chip>
              <v-chip
                v-else-if="item.sourceAction === 'IMPORT'"
                size="x-small"
                variant="tonal"
                color="warning"
              >
                IMPORT
              </v-chip>
            </div>
          </template>

          <template v-slot:item.comment="{ item }">
            <div class="tpsv2-hist-comment">
              {{ item.comment || '-' }}
              <v-tooltip
                v-if="item.comment && item.comment.length > 40"
                activator="parent"
                location="top"
                max-width="360"
              >
                {{ item.comment }}
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.createdDate="{ item }">
            {{ fmtDate(item.createdDate) }}
          </template>

          <template v-slot:item.createdBy="{ item }">
            {{ item.createdBy || '-' }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon
              variant="text"
              size="small"
              density="comfortable"
              color="primary"
              :disabled="item.isCurrent"
              :data-testid="`tpsv2-history-restore-${item.snapshotID}`"
              :aria-label="`กู้คืนรอบที่ ${item.snapshotNo}`"
              @click="openRestore(item)"
            >
              <v-icon size="19">mdi-restore</v-icon>
              <v-tooltip activator="parent" location="top">
                {{ item.isCurrent ? 'นี่คือรอบล่าสุด (ค่าปัจจุบัน)' : 'กู้คืนรอบนี้' }}
              </v-tooltip>
            </v-btn>
          </template>

          <template v-slot:no-data>
            <div class="tpsv2-empty" data-testid="tpsv2-history-empty">
              <v-icon size="30" class="tpsv2-empty-icon">mdi-history</v-icon>
              <h3 class="tpsv2-empty-title">ยังไม่มีประวัติการบันทึก</h3>
              <p class="tpsv2-empty-text">
                กดบันทึกในหน้าแก้ไขเวอร์ชันนี้ แล้วประวัติจะเริ่มถูกเก็บตั้งแต่รอบนั้นเป็นต้นไป
              </p>
            </div>
          </template>
        </v-data-table>
      </v-card-text>

      <v-divider />
      <v-card-actions class="tpsv2-foot">
        <v-btn
          v-if="selected.length === 2"
          color="info"
          variant="flat"
          prepend-icon="mdi-compare"
          data-testid="tpsv2-history-compare"
          @click="openCompare"
        >
          เทียบ 2 รอบที่เลือก
        </v-btn>
        <v-chip
          v-else-if="selected.length > 0"
          size="small"
          color="warning"
          variant="tonal"
          data-testid="tpsv2-history-compare-hint"
        >
          เลือกให้ครบ 2 รอบเพื่อเทียบ
        </v-chip>
        <v-spacer />
        <v-btn variant="outlined" data-testid="tpsv2-history-close" @click="close">
          <v-icon start>mdi-close</v-icon>
          ปิด
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- ── restore confirm (nested — never window.confirm: it blocks the page) ── -->
    <v-dialog
      v-model="restoreDialog"
      max-width="600px"
      persistent
      data-testid="tpsv2-history-restore-dialog"
    >
      <v-card class="tpsv2-sub">
        <div class="tpsv2-head tpsv2-head--sub bg-primary text-white">
          <div class="tpsv2-head-mark"><v-icon size="19">mdi-restore</v-icon></div>
          <div class="tpsv2-head-text">
            <h2 class="tpsv2-head-title">ยืนยันการกู้คืน</h2>
            <p class="tpsv2-head-sub">เขียนทับค่าปัจจุบันของเวอร์ชันนี้ด้วยรอบ v{{ restoreNo }}</p>
          </div>
        </div>
        <div class="tpsv2-subbody">
          <v-alert
            v-if="restoreError"
            type="error"
            variant="tonal"
            density="compact"
            class="tpsv2-alert"
            data-testid="tpsv2-history-restore-error"
          >
            {{ restoreError }}
          </v-alert>

          <v-alert type="info" variant="tonal" density="compact" class="tpsv2-alert">
            ค่าทุกช่องของเวอร์ชันนี้จะถูกเขียนทับด้วยข้อมูลของ <strong>v{{ restoreNo }}</strong> —
            รหัสเวอร์ชัน คำอธิบาย และสถานะการใช้งานไม่ถูกแตะ · ประวัติเดิมยังอยู่ครบ
            และการกู้คืนจะถูกบันทึกเป็นรอบใหม่ จึงย้อนกลับได้อีก
          </v-alert>

          <v-alert
            v-if="versionActive"
            type="warning"
            variant="tonal"
            density="compact"
            icon="mdi-alert"
            class="tpsv2-alert"
            data-testid="tpsv2-history-live-warning"
          >
            เวอร์ชันนี้กำลังใช้งานจริง — กู้คืนแล้วมีผลกับ Tax Planning V2 ที่ทำงานอยู่ทันที
          </v-alert>

          <div class="tpsv2-field">
            <div class="tpsv2-field-head"><span class="tpsv2-field-label">หมายเหตุ (ไม่บังคับ)</span></div>
            <v-textarea
              v-model="restoreComment"
              variant="outlined"
              density="compact"
              rows="3"
              maxlength="500"
              counter="500"
              hide-details="auto"
              aria-label="หมายเหตุการกู้คืน"
              data-testid="tpsv2-history-restore-comment"
            />
          </div>

          <!-- The LIVE version needs a deliberate acknowledgement, not just a second click. -->
          <v-checkbox
            v-if="versionActive"
            v-model="liveAck"
            color="warning"
            density="comfortable"
            hide-details
            label="ฉันเข้าใจว่านี่คือเวอร์ชันที่ใช้งานจริง"
            data-testid="tpsv2-history-live-ack"
          />
        </div>
        <v-divider />
        <v-card-actions class="tpsv2-foot">
          <v-spacer />
          <v-btn
            variant="outlined"
            :disabled="restoring"
            data-testid="tpsv2-history-restore-cancel"
            @click="closeRestore"
          >
            <v-icon start>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :loading="restoring"
            :disabled="versionActive && !liveAck"
            data-testid="tpsv2-history-restore-confirm"
            @click="confirmRestore"
          >
            <v-icon start>mdi-restore</v-icon>
            ยืนยันกู้คืน
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <tax-settings-history-compare-v2-dialog
      v-model="compareDialog"
      :left-snapshot-id="compareIds[0]"
      :right-snapshot-id="compareIds[1]"
    />
  </v-dialog>
</template>

<script>
/**
 * Save-history dialog of ONE Tax Planning Settings V2 version — the list of snapshots taken on
 * every successful save, with «restore» per row and «compare» across two ticked rows.
 *
 * Contract with the parent (`TaxPlanningSettingsV2Dialog.vue`):
 *   - the parent owns the version identity (`settingsVersionId`) and the snackbar;
 *   - this dialog reports its own errors inline (`v-alert`), never through a snackbar of its own;
 *   - `restored` fires AFTER the API call succeeded, carrying
 *     `{ settingsID, restoredFromNo, warnings[] }` so the parent can RELOAD the editor — a restore
 *     rewrites the columns under an open form, and a form left showing the pre-restore values is a
 *     screen that lies about the database.
 *
 * The list is (re)loaded every time the dialog opens, and again right after a successful restore —
 * a restore is itself recorded as a new snapshot, so the table must not show a stale «Current».
 *
 * 🔴 `createdDate` comes off the wire WITHOUT a `Z` (see `utils/thaiTime.js`): rendering it with
 * `new Date()` would silently show every row 7 hours early.
 */
import { taxPlanningSettingsV2Api } from '@/services/taxPlanningSettingsV2Api';
import { fmtThaiDateTime } from '@/utils/thaiTime';
import TaxSettingsHistoryCompareV2Dialog from '@/components/TaxSettingsHistoryCompareV2Dialog.vue';

export default {
  name: 'TaxSettingsHistoryV2Dialog',
  components: { TaxSettingsHistoryCompareV2Dialog },
  props: {
    modelValue: { type: Boolean, default: false },
    /** settings version whose history is listed (`TaxPlanningSettingsV2Dto.id`) */
    settingsVersionId: { type: Number, default: null },
    /** shown in the subtitle (e.g. `V1`) */
    versionLabel: { type: String, default: '' },
    /** true when this version is the ACTIVE one — gates the restore confirm behind an ack */
    versionActive: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'restored'],
  data() {
    return {
      loading: false,
      error: '',
      rows: [],
      /** array of `snapshotID` (v-data-table show-select binds item-value, not objects) */
      selected: [],
      compareDialog: false,
      compareIds: [null, null],
      restoreDialog: false,
      restoreTarget: null,
      restoreComment: '',
      liveAck: false,
      restoring: false,
      restoreError: '',
      headers: [
        { title: 'รอบ', key: 'snapshotNo', align: 'center', sortable: true, width: '170px' },
        { title: 'หมายเหตุ', key: 'comment', align: 'start', sortable: false },
        { title: 'เมื่อ', key: 'createdDate', align: 'center', sortable: true, width: '160px' },
        { title: 'โดย', key: 'createdBy', align: 'center', sortable: false, width: '140px' },
        { title: 'จัดการ', key: 'actions', align: 'center', sortable: false, width: '90px' },
      ],
    };
  },
  computed: {
    /** `v{N}` of the row being restored — used by the confirm dialog's copy. */
    restoreNo() {
      return this.restoreTarget ? this.restoreTarget.snapshotNo : '';
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.load();
      else this.closeRestore();
    },
  },
  methods: {
    fmtDate(value) {
      return fmtThaiDateTime(value, '-');
    },
    async load() {
      if (!this.settingsVersionId) {
        this.rows = [];
        this.selected = [];
        this.error = 'ยังไม่รู้ว่าจะดูประวัติของเวอร์ชันไหน — ปิดหน้าต่างแล้วกดปุ่มประวัติใหม่อีกครั้ง';
        return;
      }
      this.loading = true;
      this.error = '';
      try {
        const data = await taxPlanningSettingsV2Api.listHistory(this.settingsVersionId);
        this.rows = Array.isArray(data) ? data : [];
        // Drop ticks that point at rows the reload no longer has (retention prune / restore).
        const alive = new Set(this.rows.map((r) => r.snapshotID));
        this.selected = this.selected.filter((id) => alive.has(id));
      } catch (e) {
        this.rows = [];
        this.selected = [];
        this.error = (e && e.message) || 'โหลดประวัติการบันทึกไม่สำเร็จ';
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    openCompare() {
      if (this.selected.length !== 2) return;
      // Order does not matter here — the compare dialog puts the lower snapshotNo on the left.
      this.compareIds = [this.selected[0], this.selected[1]];
      this.compareDialog = true;
    },
    openRestore(item) {
      this.restoreTarget = item;
      this.restoreComment = `Restored from v${item.snapshotNo}`;
      this.liveAck = false;
      this.restoreError = '';
      this.restoreDialog = true;
    },
    closeRestore() {
      this.restoreDialog = false;
      this.restoreTarget = null;
      this.restoreComment = '';
      this.liveAck = false;
      this.restoreError = '';
    },
    async confirmRestore() {
      if (!this.restoreTarget || this.restoring) return;
      if (this.versionActive && !this.liveAck) return;
      this.restoring = true;
      this.restoreError = '';
      try {
        const comment = (this.restoreComment || '').trim();
        const fromNo = this.restoreTarget.snapshotNo;
        const result = await taxPlanningSettingsV2Api.restoreSnapshot(this.restoreTarget.snapshotID, {
          comment: comment || null,
        });
        // The parent reloads the editor off this — hand it the round it came from even if the
        // backend result ever stops echoing it.
        this.$emit('restored', Object.assign({ restoredFromNo: fromNo }, result || {}));
        this.closeRestore();
        await this.load();
      } catch (e) {
        this.restoreError = (e && e.message) || 'กู้คืนไม่สำเร็จ';
      } finally {
        this.restoring = false;
      }
    },
  },
};
</script>

<style scoped>
/**
 * 🔴 The `--tps-*` ramp is declared HERE as well as in `TaxPlanningSettingsV2Dialog.vue`: this
 * dialog is teleported into its own overlay, so it is not a descendant of `.tpsv2` and would
 * otherwise resolve every var to nothing (no borders, no text colour).
 */
.tpsv2-sub {
  --tps-ink: rgba(var(--v-theme-on-surface), 0.94);
  --tps-ink-2: rgba(var(--v-theme-on-surface), 0.75);
  --tps-ink-3: rgba(var(--v-theme-on-surface), 0.66);
  --tps-line: rgba(var(--v-theme-on-surface), 0.1);
  --tps-line-soft: rgba(var(--v-theme-on-surface), 0.06);
  --tps-panel: rgba(var(--v-theme-on-surface), 0.02);
  --tps-panel-2: rgba(var(--v-theme-on-surface), 0.045);
  --tps-gutter: 20px;
  display: flex;
  flex-direction: column;
}

.tpsv2-head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px var(--tps-gutter);
}

.tpsv2-head-mark {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.16);
}

.tpsv2-head-text { flex: 1 1 auto; min-width: 0; }

/* The global reset zeroes heading weight, so titles set their own. */
.tpsv2-head-title { font-size: 1.05rem; font-weight: 600; line-height: 1.35; }
.tpsv2-head-sub { font-size: 0.75rem; line-height: 1.4; opacity: 0.82; }

.tpsv2-livetag {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  font-size: 0.75rem;
  font-weight: 600;
}

.tpsv2-livedot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.22);
}

.tpsv2-hist-body { padding: 16px var(--tps-gutter) 4px; }
.tpsv2-subbody {
  padding: 20px var(--tps-gutter) 8px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.tpsv2-alert { font-size: 0.8rem; line-height: 1.6; }
.tpsv2-hist-table { font-size: 0.82rem; }

.tpsv2-hist-comment {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tpsv2-field-head { margin-bottom: 6px; }
.tpsv2-field-label { font-size: 0.8rem; font-weight: 600; color: var(--tps-ink); }

.tpsv2-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 24px 44px;
  text-align: center;
}

.tpsv2-empty-icon { color: var(--tps-ink-3); }
.tpsv2-empty-title { font-size: 0.9rem; font-weight: 600; color: var(--tps-ink); }

.tpsv2-empty-text {
  max-width: 52ch;
  font-size: 0.8rem;
  line-height: 1.6;
  color: var(--tps-ink-3);
}

.tpsv2-foot {
  flex: none;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px var(--tps-gutter);
}
</style>
